import { useEffect, useState } from "react";

import "./DiaryTimeCard.css";
import Images from "../imagesComponent/Images";
import Productivity from "../productivityCard/Productivity";
import DefaultScreenshot from "../../medias/images/noScreenshot.jpg";
import DefaultWebcam from "../../medias/images/noCamshot.jpg"
import TimeCardModal from "../diaryTimeCardModal/TimeCardModal";
import Backdrop from "../backdrop/Backdrop";
import LoadingSpinner from "../loadSpinner/LoadingSpinner";
import { Tag } from "primereact/tag";
import { FaInfoCircle } from "react-icons/fa";
import { requestGetImagesFromBucket } from "../../services/wisdomServices/trackingService";
import { getSessionItem } from "../../utils/helper/session";

const imageCache = new Map();


/**
 * @function diaryTimeCard
 * @param {Object} props - component props
 * @param {string|number} props.id - timecard unique identifier
 * @param {number} props.status - timecard status index
 * @param {string} props.activitytime - tracked activity time (formatted)
 * @param {string} [props.screensrc] - screenshot image reference
 * @param {string} [props.camsrc] - webcam image reference
 * @param {string} [props.complianceFlag] - compliance flag label
 * @param {Function} props.handleSelection - callback when timecard is selected
 * @returns {JSX.Element} - renders a diary time card entry with activity details, screenshots, webcam images and selection controls
 */
function DiaryTimeCard(props) {
  /**
   * @type {Object}
   */
  const user = getSessionItem("User");
  /**
   * @type {[boolean, Function]}
   */
  const [timeCardModalIsOpen, setTimeCardModalIsOpen] = useState(false);
  /**
   * @type {[boolean, Function]}
   */
  const [timeCardIsSelected, setTimeCardIsSelected] = useState(false);
  /**
   * @type {[string, Function]}
   */
  const [screenshot, setScreenshot] = useState();
  /**
   * @type {[string, Function]}
   */
  const [webcam, setWebcam] = useState();
  /**
   * @type {Array<string>}
   */
  const [loading, setLoading] = useState(true);

  function openCardDetails() {
    setTimeCardModalIsOpen(true);
  }

  function closeModalHandler() {
    setTimeCardModalIsOpen(false);
  }
  /**
   * @function ToggleTimeCard
   * @param {Event} e - checkbox change evente
   * @description - toggles the selection state of the timecard and notifies the parent component
   */
  function ToggleTimeCard(e) {
    setTimeCardIsSelected(!timeCardIsSelected);
    props.handleSelection(e.target.checked, e.target.id);
  }

  /**
 * @async
 * @function fetchScreenshot
 * @param {Object} props
 * @returns {Promise<string>} image url
 */
  const fetchScreenshot = async (props) => {
    try {
      if (!props?.screensrc || props.screensrc === "default") {
        return DefaultScreenshot;
      }

      if (imageCache.has(props.screensrc)) {
        return imageCache.get(props.screensrc);
      }
      
      const response = await requestGetImagesFromBucket(
        user.id,
        props.screensrc,
        "screenshot"
      );

      if (!response) {
        return DefaultScreenshot;
      }

      const url = URL.createObjectURL(response);
      imageCache.set(props.screensrc, url);
      return url;

    } catch (error) {
      console.error("Error fetching screenshot:", error);
      return DefaultScreenshot;
    }
  };

  /**
   * @async
   * @function fetchWebcam
   * @param {Object} props
   * @returns {Promise<string>} image url
   */
  const fetchWebcam = async (props) => {
    try {
      if (!props?.camsrc || props.camsrc === "default") {
        return DefaultWebcam;
      }

      if (imageCache.has(props.camsrc)) {
        return imageCache.get(props.camsrc);
      }

      const response = await requestGetImagesFromBucket(
        user.id,
        props.camsrc,
        "webcam"
      );

      if (!response) {
        return DefaultWebcam;
      }

      const url = URL.createObjectURL(response);
      imageCache.set(props.camsrc, url);
      return url;
      
    } catch (error) {
      console.error("Error fetching webcam:", error);
      return DefaultWebcam;
    }
  };


  /**
   * @async
   * @function fetchImages
   * @param {Object} props - timecard props
  */
  const fetchImages = async (props) => {
    const [scshot, wcam] = await Promise.all([
      fetchScreenshot(props),
      fetchWebcam(props),
    ]);

    setScreenshot(scshot);
    setWebcam(wcam);
  };

  /**
   * @function renderActions   
   * @returns {JSX.Element} - renders action controls based on the timecard status
   */
  const renderActions = () => {
    if (props.status == "rejected") {
      return (
        <>
          <div>
            <FaInfoCircle title="You cannot update a Rejected timecard, please reach out to your manager" />
          </div>
          <div className="activityTime">{props.activitytime}</div>
          {props.complianceFlag && props.complianceFlag !== "Geofence" && (
            <Tag severity="warning">Compliance</Tag>
          )}
          {props.complianceFlag === "Geofence" && (
            <Tag severity="warning">Geofence</Tag>
          )}
        </>
      );
    }

    if (props.status == "otDiscard") {
      return (
        <>
          <div>
            <FaInfoCircle title="You cannot update an OT Discard timecard, please reach out to your manager" />
          </div>
          <div className="activityTime">{props.activitytime}</div>
        </>
      );
    }

    return (
      <>
        <input
          style={{ justifySelf: "flex-start" }}
          type="checkbox"
          className="cards-checkboxes"
          onChange={ToggleTimeCard}
          id={props.id}
        />
        <div className="activityTime">{props.activitytime}</div>
        {props.complianceFlag && props.complianceFlag !== "Geofence" && (
          <Tag severity="warning">Compliance</Tag>
        )}
        {props.complianceFlag === "Geofence" && (
          <Tag severity="warning">Geofence</Tag>
        )}
      </>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchImages(props);
        setLoading(false);
      } catch (error) {
        console.error("ERROR: ", error);
      }
    };

    fetchData();
  }, [props.screensrc, props.camsrc, props.id]);

  useEffect(() => {
    return () => {
      if (screenshot?.startsWith("blob:")) {
        URL.revokeObjectURL(screenshot);
      }
      if (webcam?.startsWith("blob:")) {
        URL.revokeObjectURL(webcam);
      }
    };
  }, []);

  return (
    <>
      {!loading ? (
        <>
          {user.roleName == "Agent" ? (
            <div className="diaryTimeCard">
              <div
                className="diaryTimeCard-Actions"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {renderActions()}
              </div>
              <Productivity
                project={props.project}
                mouse={props.productivityMouse}
                keys={props.productivityKeys}
                productivityType={props.status}
              />
              <div className="diary-shots" onClick={openCardDetails}>
                <Images imageSRC={screenshot} />
                <Images imageSRC={webcam} />
              </div>

              {timeCardModalIsOpen && (
                <TimeCardModal
                  userId={user.id}
                  activityTime={props.activitytime}
                  project={props.project}
                  appInUse={props.appInUse}
                  tabInUse={props.tabInUse}
                  mouse={props.productivityMouse}
                  keys={props.productivityKeys}
                  productivityType={props.status}
                  screensrc={props.screensrc}
                  camsrc={props.camsrc}
                  onCancel={closeModalHandler}
                />
              )}
              {timeCardModalIsOpen && <Backdrop onCancel={closeModalHandler} />}
            </div>
          ) : (
            <div className="diaryTimeCard">
              <div
                className="diaryTimeCard-Actions"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 8px",
                }}
              >
                <input
                  style={{ justifySelf: "flex-start" }}
                  type="checkbox"
                  className="cards-checkboxes"
                  onChange={ToggleTimeCard}
                  id={props.id}
                />
                <div className="activityTime">{props.activitytime}</div>
                {props.complianceFlag &&
                  props.complianceFlag !== "Geofence" && (
                    <Tag severity={"warning"}>Compliance</Tag>
                  )}
                {props.complianceFlag &&
                  props.complianceFlag === "Geofence" && (
                    <Tag severity={"warning"}>Geofence</Tag>
                  )}
              </div>
              <Productivity
                project={props.project}
                mouse={props.productivityMouse}
                keys={props.productivityKeys}
                productivityType={props.status}
              />
              <div className="diary-shots" onClick={openCardDetails}>
                <Images imageSRC={screenshot} />
                <Images imageSRC={webcam} />
              </div>

              {timeCardModalIsOpen && (
                <TimeCardModal
                  userId={user.id}
                  activityTime={props.activitytime}
                  project={props.project}
                  appInUse={props.appInUse}
                  tabInUse={props.tabInUse}
                  mouse={props.productivityMouse}
                  keys={props.productivityKeys}
                  productivityType={props.status}
                  screensrc={props.screensrc}
                  camsrc={props.camsrc}
                  date={props.date}
                  onCancel={closeModalHandler}
                />
              )}
              {timeCardModalIsOpen && <Backdrop onCancel={closeModalHandler} />}
            </div>
          )}
        </>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
}

export default DiaryTimeCard;
